import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../../lib/utils";

export type AvatarSize = 16 | 20 | 24 | 28 | 32 | 40 | 48 | 120;

export interface AvatarProps {
	/**
	 * Size of the avatar
	 */
	size?: AvatarSize;
	/**
	 * The alt attribute for the avatar image. Describes who the avatar represents.
	 *
	 * **Accessibility Guidelines**:
	 * - Include the person's name: "Sarah Johnson's avatar"
	 * - Keep it concise: avoid "Picture of" or "Image of"
	 * - Use empty string "" ONLY if the name appears as visible text next to the avatar
	 * - For anonymous users: "Anonymous user avatar" (handled automatically if omitted)
	 *
	 * **Examples**:
	 * ```tsx
	 * // Good - descriptive
	 * <Avatar id="123" alt="John Doe's avatar" />
	 *
	 * // Good - decorative (name visible next to avatar)
	 * <div>
	 *   <Avatar id="123" alt="" />
	 *   <span>John Doe</span>
	 * </div>
	 *
	 * // Avoid - too generic
	 * <Avatar id="123" alt="Avatar" />
	 * <Avatar id="123" alt="Employee picture" />
	 * ```
	 *
	 * @see https://www.w3.org/WAI/tutorials/images/informative/
	 */
	alt?: string;
	/**
	 * The id of the user to render the avatar for. If omitted, shows anonymous avatar.
	 */
	id?: string;
	/**
	 * Border around the avatar
	 */
	border?: boolean;
	/**
	 * Classname to override the default styles.
	 * ⚠️ Use sparingly - prefer using the `size` and `border` props.
	 */
	dangerouslySetClassName?: string;
	/**
	 * Custom URL for the avatar image. Overrides the default avatar URL generation.
	 * Useful for external users or custom avatar sources.
	 */
	url?: string;
	/**
	 * Test ID for testing purposes. Will be added as data-testid attribute.
	 */
	testId?: string;
	/**
	 * Title attribute for hover text. Provides additional context on hover.
	 */
	title?: string;
}

const getAvatarURL = (id: string) => {
	const mediaCDN = `${
		window.PERFORMYARD_MEDIA_CDN ? window.PERFORMYARD_MEDIA_CDN : "https://performyard-content.performyard.com/"
	}`;

	return `${mediaCDN}avatar/${id}/`;
};

/**
 * Component for rendering a user's avatar image. If no id is provided, a default anonymous avatar will be rendered.
 */
const avatarVariants = cva("size-5 rounded-full object-cover", {
	variants: {
		size: {
			16: "size-4",
			20: "size-5",
			24: "size-6",
			28: "size-7",
			32: "size-8",
			40: "size-10",
			48: "size-12",
			120: "size-[120px]",
		},
		border: {
			true: "border border-solid border-neutral-10",
		},
	},
	defaultVariants: {
		size: 40,
		border: false,
	},
});

export const Avatar = (props: AvatarProps) => {
	const { size = 40, id = "", dangerouslySetClassName, border, url, testId, title } = props;

	const anonymousAvatarURL = "https://dev-media.performyard.com/static/img/anonymous.png";

	// Determine image source
	let src = "";
	if (url) {
		// Custom URL takes precedence
		src = url;
	} else if (!id) {
		src = anonymousAvatarURL;
	} else {
		src = getAvatarURL(id);
	}

	// Compute smart default alt text based on context
	let computedAlt = props.alt;
	if (computedAlt === undefined) {
		// No alt provided - use smart defaults
		if (!id && !url) {
			computedAlt = "Anonymous user avatar";
		} else {
			computedAlt = "User avatar";
		}

		// Development warning for missing descriptive alt text
		if (process.env.NODE_ENV === "development" && id) {
			console.warn(
				`Avatar: Missing descriptive alt text for user ${id}. ` +
					`Please provide alt text like "John Doe's avatar" for better accessibility.`
			);
		}
	}

	return (
		<img
			className={cn(avatarVariants({ size, border }), dangerouslySetClassName)}
			alt={computedAlt}
			src={src}
			title={title}
			data-testid={testId}
		/>
	);
};
